// UI 관련 상수
export const SIDEBAR_WIDTH = 300;
export const HEADER_HEIGHT = 56; // px, YouTube와 동일
export const SPACING = 24; // 컬럼 간 gap(px)
export const PADDING = 0;  // 전체 패딩(px)
export const MAX_CONTENT_WIDTH = 1200; // 메인 컨텐츠 최대 너비(예시)
export const DEFAULT_BORDER_RADIUS = 6;
export const DEFAULT_BOX_SHADOW = '0 0 20px rgba(0,0,0,0.05)';
export const PRIMARY_COLOR = '#1976d2';
export const SECONDARY_COLOR = '#f5f5f5';
export const BORDER_COLOR = '#ccc';
export const FONT_FAMILY = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
export const FONT_SIZE_BASE = 14;
export const FONT_SIZE_SMALL = 12;
export const FONT_SIZE_MEDIUM = 16;
export const FONT_SIZE_LARGE = 20;
export const FONT_SIZE_XL = 24;
export const Z_INDEX_HEADER = 1200;
export const Z_INDEX_DRAWER = 1100;
export const ANIMATION_DURATION = 0.2; // 초 단위

// 환경별 API URL 설정
// Capacitor 환경 감지를 더 정확하게 (실제 네이티브 환경인지 확인)
const isCapacitor = !!(window.Capacitor?.isNative);
// 개발 환경 감지 (Vite 개발 서버 또는 localhost)
const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// iOS 시뮬레이터 감지 (더 정교한 환경 분기)
const isIOSSimulator = isCapacitor && window.Capacitor?.platform === 'ios' && 
  (navigator.userAgent.includes('Simulator') || window.location.hostname === 'localhost');

// 개발 환경 강제 설정 (개발자가 수동으로 설정 가능)
const forceLocalDev = localStorage.getItem('FORCE_LOCAL_DEV') === 'true';

// 웹 개발 환경 또는 iOS 시뮬레이터 개발 환경
const isWebDevelopment = (isDevelopment && !isCapacitor) || isIOSSimulator || forceLocalDev;

// 로컬 개발 환경 (웹 + iOS 시뮬레이터)
const LOCAL_TRANSCRIPT_API_URL = 'http://localhost:8000';
const LOCAL_AI_API_URL = 'http://localhost:8000';

// 서버 환경 (Production & iOS 실제 디바이스)
const PROD_TRANSCRIPT_API_URL = 'https://api.tubedigest.ai';  // 윈도우 랩탑 (자막 전용)
const PROD_AI_API_URL = 'https://tubedigest-api.onrender.com';  // Render.com (AI 서비스)

// 환경에 따른 API URL 설정
// 웹 개발 환경이나 iOS 시뮬레이터에서는 로컬 URL, 그 외에는 프로덕션 URL 사용
export const TRANSCRIPT_API_URL = isWebDevelopment ? LOCAL_TRANSCRIPT_API_URL : PROD_TRANSCRIPT_API_URL;
export const AI_API_URL = isWebDevelopment ? LOCAL_AI_API_URL : PROD_AI_API_URL;

// 기본 API URL (하위 호환성)
export const API_BASE_URL = AI_API_URL;

// 디버깅 정보 출력 (개발 환경에서만 상세 정보)
if (isDevelopment) {
  console.log('🔍 Environment Debug Info:', {
    isCapacitor,
    isDevelopment,
    isWebDevelopment,
    isIOSSimulator,
    forceLocalDev,
    environment: isCapacitor ? 
      (isIOSSimulator ? 'iOS Simulator (Dev)' : 'iOS Device (Prod)') : 
      (isWebDevelopment ? 'Web Development' : 'Web Production'),
    windowLocation: window.location.href,
    capacitorPlatform: window.Capacitor?.platform,
    capacitorIsNative: window.Capacitor?.isNative
  });
  console.log('🌐 API URLs:', {
    TRANSCRIPT_API_URL,
    AI_API_URL,
    API_BASE_URL
  });
} else {
  // 프로덕션에서는 간단한 정보만
  console.log('🚀 TubeDigest 환경:', isCapacitor ? 'Mobile App' : 'Web App');
}

// iOS 개발자를 위한 설정 도구 추가
if (isCapacitor && isDevelopment) {
  window.setLocalDev = (enable) => {
    localStorage.setItem('FORCE_LOCAL_DEV', enable.toString());
    console.log(`🔧 로컬 개발 모드 ${enable ? '활성화' : '비활성화'}됨. 앱을 새로고침하세요.`);
  };
  console.log('🛠️ iOS 개발자 도구: window.setLocalDev(true/false)로 로컬 서버 사용 가능');
}

// 요약(핵심요약/챕터별 정리) 섹션 스타일
export const SUMMARY_SECTION_STYLE = {
  bgcolor: '#fff',
  borderRadius: DEFAULT_BORDER_RADIUS,
  boxShadow: DEFAULT_BOX_SHADOW,
  p: 3,
};

// 답변(대화) 섹션 스타일
export const ANSWER_SECTION_STYLE = {
  bgcolor: '#f8fafc',
  borderRadius: DEFAULT_BORDER_RADIUS,
  boxShadow: '0 0 10px rgba(25,118,210,0.07)',
  border: '1px solid #e3e8ef',
  p: 2.5,
  fontSize: FONT_SIZE_BASE + 1,
  color: '#222',
};

// 다국어 지원 언어 설정
export const SUPPORTED_LANGUAGES = [
  { 
    code: 'en', 
    name: 'English', 
    nativeName: 'English',
    flag: '🇺🇸'
  },
  { 
    code: 'es', 
    name: 'Spanish', 
    nativeName: 'Español',
    flag: '🇪🇸'
  },
  { 
    code: 'de', 
    name: 'Deutsch', 
    nativeName: 'Deutsch',
    flag: '🇩🇪'
  },
  { 
    code: 'fr', 
    name: 'French', 
    nativeName: 'Français',
    flag: '🇫🇷'
  },
  { 
    code: 'zh', 
    name: '中文/汉语', 
    nativeName: '中文/汉语',
    flag: '🇨🇳'
  },
  { 
    code: 'ja', 
    name: '日本語', 
    nativeName: '日本語',
    flag: '🇯🇵'
  },
  { 
    code: 'ko', 
    name: '한국어', 
    nativeName: '한국어',
    flag: '🇰🇷',
    isDefault: true
  },
  { 
    code: 'hi', 
    name: 'Hindi', 
    nativeName: 'हिंदी',
    flag: '🇮🇳'
  }
];

// 기본 언어
export const DEFAULT_LANGUAGE = 'en';

// 언어 코드로 언어 정보 찾기
export const getLanguageByCode = (code) => {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code) || SUPPORTED_LANGUAGES[0];
};

// 국가 코드를 언어 코드로 매핑 (IP 기반 지역 감지용)
export const COUNTRY_TO_LANGUAGE = {
  'US': 'en',  // 미국
  'GB': 'en',  // 영국
  'CA': 'en',  // 캐나다
  'AU': 'en',  // 호주
  'ES': 'es',  // 스페인
  'MX': 'es',  // 멕시코
  'AR': 'es',  // 아르헨티나
  'DE': 'de',  // 독일
  'FR': 'fr',  // 프랑스
  'BE': 'fr',  // 벨기에
  'CH': 'fr',  // 스위스
  'CN': 'zh',  // 중국
  'TW': 'zh',  // 대만
  'HK': 'zh',  // 홍콩
  'JP': 'ja',  // 일본
  'KR': 'ko',  // 한국
  'IN': 'hi'   // 인도
};

// 기본 언어로 폴백
export const mapCountryToLanguage = (countryCode) => {
  return COUNTRY_TO_LANGUAGE[countryCode?.toUpperCase()] || DEFAULT_LANGUAGE;
}; 

// 언어 상수
export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Español', nativeName: 'Español' },
  { code: 'de', name: 'Deutsch', nativeName: 'Deutsch' },
  { code: 'fr', name: 'Français', nativeName: 'Français' },
  { code: 'zh', name: '中文', nativeName: '中文' },
  { code: 'ja', name: '日本語', nativeName: '日本語' },
  { code: 'ko', name: '한국어', nativeName: '한국어' },
  { code: 'hi', name: 'हिंदी', nativeName: 'हिंदी' }
];

// 언어 매핑
export const LANGUAGE_MAP = {
  en: 'English', 
  es: 'Spanish',
  de: 'German',
  fr: 'French',
  zh: 'Chinese',
  ja: 'Japanese',
  ko: 'Korean',
  hi: 'Hindi'
};

// API 엔드포인트
export const API_ENDPOINTS = {
  ANALYZE: '/api/analyze',
  SUMMARY: '/api/summary',
  CHAPTERS: '/api/chapters',
  QA: '/api/qa',
  HISTORY: '/api/history'
};

// 분석 상태
export const ANALYSIS_STATUS = {
  IDLE: 'idle',
  ANALYZING: 'analyzing',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

// 로컬 스토리지 키
export const STORAGE_KEYS = {
  LANGUAGE: 'tubedigest_language',
  THEME: 'tubedigest_theme',
  USER_PREFERENCES: 'tubedigest_preferences'
}; 